import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { interviewsApi, formatApiError, resolveImageUrl } from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Mic, Plus, Pencil, Trash2, Star, StarOff, Eye, EyeOff, Upload, X,
  ArrowLeft, ImagePlus, Loader2, ExternalLink,
} from 'lucide-react';

const EMPTY_FORM = {
  driver_name: '',
  team_name: '',
  subtitle: '',
  location: '',
  sponsor_name: '',
  sponsor_url: '',
  sponsor_tagline: '',
  gallery_title: '',
  hero_image: '',
  gallery_images: [],
  qa: [{ q: '', a: '' }],
  is_published: true,
  is_featured: false,
};

export default function AdminInterviewsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // id if editing, null if creating
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const heroInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [savedId, setSavedId] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    fetchList();
  }, [user, navigate]);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await interviewsApi.adminList();
      setItems(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setSavedId(null);
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item.id);
    setForm({
      driver_name: item.driver_name || '',
      team_name: item.team_name || '',
      subtitle: item.subtitle || '',
      location: item.location || '',
      sponsor_name: item.sponsor_name || '',
      sponsor_url: item.sponsor_url || '',
      sponsor_tagline: item.sponsor_tagline || '',
      gallery_title: item.gallery_title || '',
      hero_image: item.hero_image || '',
      gallery_images: item.gallery_images || [],
      qa: (item.qa && item.qa.length > 0 ? item.qa : [{ q: '', a: '' }]).map(x => ({ q: x.q || x.question || '', a: x.a || x.answer || '' })),
      is_published: !!item.is_published,
      is_featured: !!item.is_featured,
    });
    setSavedId(item.id);
    setError('');
    setDialogOpen(true);
  };

  const updateQa = (i, field, val) => {
    setForm((f) => ({ ...f, qa: f.qa.map((q, idx) => idx === i ? { ...q, [field]: val } : q) }));
  };
  const addQa = () => setForm((f) => ({ ...f, qa: [...f.qa, { q: '', a: '' }] }));
  const removeQa = (i) => setForm((f) => ({ ...f, qa: f.qa.filter((_, idx) => idx !== i) }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        qa: form.qa.filter((x) => x.q.trim() || x.a.trim()),
      };
      let id = savedId;
      if (id) {
        await interviewsApi.update(id, payload);
      } else {
        const res = await interviewsApi.create(payload);
        id = res.data.id;
        setSavedId(id);
        setEditing(id);
      }
      await fetchList();
      return id;
    } catch (err) {
      setError(formatApiError(err));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleUploadImage = async (kind, file) => {
    let id = savedId;
    if (!id) {
      // Save first to get an id
      id = await handleSave({ preventDefault: () => {} });
      if (!id) return;
    }
    try {
      const res = await interviewsApi.uploadImage(id, kind, file);
      if (kind === 'hero') {
        setForm((f) => ({ ...f, hero_image: res.data.image_url }));
      } else {
        setForm((f) => ({ ...f, gallery_images: [...(f.gallery_images || []), res.data.image_url] }));
      }
      await fetchList();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleHeroFile = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUploadImage('hero', file);
    e.target.value = '';
  };
  const handleGalleryFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) await handleUploadImage('gallery', f);
    e.target.value = '';
  };

  const removeGallery = async (idx) => {
    setForm((f) => ({ ...f, gallery_images: f.gallery_images.filter((_, i) => i !== idx) }));
    if (savedId) {
      const next = form.gallery_images.filter((_, i) => i !== idx);
      await interviewsApi.update(savedId, { gallery_images: next });
      await fetchList();
    }
  };

  const toggleFeatured = async (item) => {
    try {
      await interviewsApi.update(item.id, { is_featured: !item.is_featured });
      await fetchList();
    } catch (err) { alert(formatApiError(err)); }
  };

  const togglePublished = async (item) => {
    try {
      await interviewsApi.update(item.id, { is_published: !item.is_published });
      await fetchList();
    } catch (err) { alert(formatApiError(err)); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.driver_name}" interview? This cannot be undone.`)) return;
    try {
      await interviewsApi.delete(item.id);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
    } catch (err) { alert(formatApiError(err)); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="bg-[#141414] border-b border-[#262626]">
        <div className="container mx-auto px-4 py-6">
          <Link to="/admin/dashboard" className="inline-flex items-center text-gray-400 hover:text-white text-sm mb-2">
            <ArrowLeft className="w-3 h-3 mr-1" /> Back to Admin Dashboard
          </Link>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="tag text-[#FF3B30] mb-2">Admin</p>
              <h1 className="font-heading font-bold text-3xl text-white">Manage Featured Interviews</h1>
              <p className="text-gray-400 text-sm mt-1">Create driver spotlights, upload photos, pick which one appears on the homepage.</p>
            </div>
            <Button onClick={openNew} className="bg-[#FF3B30] hover:bg-[#E6352B]" data-testid="new-interview-btn">
              <Plus className="w-4 h-4 mr-2" />New Interview
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {items.length === 0 ? (
          <Card className="bg-[#141414] border-[#262626] p-10 text-center">
            <Mic className="w-14 h-14 text-gray-700 mx-auto mb-4" />
            <h3 className="text-white font-heading text-xl mb-2">No interviews yet</h3>
            <p className="text-gray-400 text-sm mb-4">Create the first driver spotlight for the homepage.</p>
            <Button onClick={openNew} className="bg-[#FF3B30] hover:bg-[#E6352B]">
              <Plus className="w-4 h-4 mr-2" />Create Interview
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((item) => (
              <Card key={item.id} className="bg-[#141414] border-[#262626] overflow-hidden" data-testid={`interview-${item.id}`}>
                <div className="flex gap-4 p-4">
                  <div className="w-28 h-28 rounded overflow-hidden bg-[#1F1F1F] border border-[#262626] flex-shrink-0">
                    {item.hero_image ? (
                      <img src={resolveImageUrl(item.hero_image)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><Mic className="w-8 h-8 text-gray-700" /></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="text-white font-heading font-bold text-lg truncate">{item.driver_name}</h3>
                      {item.is_featured && (
                        <span className="tag bg-[#FF3B30]/20 text-[#FF3B30] px-2 py-0.5 rounded text-[10px] uppercase tracking-wider">
                          Homepage
                        </span>
                      )}
                      {!item.is_published && (
                        <span className="tag bg-gray-500/20 text-gray-400 px-2 py-0.5 rounded text-[10px]">Draft</span>
                      )}
                    </div>
                    {item.team_name && <p className="text-[#FF3B30] text-xs font-bold uppercase tracking-wider">{item.team_name}</p>}
                    <p className="text-gray-400 text-xs mt-1 line-clamp-2">{item.subtitle}</p>
                    <p className="text-gray-600 text-[11px] mt-2">
                      {(item.qa || []).length} Q&A · {(item.gallery_images || []).length} photos · {item.views || 0} views
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      <Button size="sm" variant="outline" className="border-[#262626] text-white h-8" onClick={() => openEdit(item)} data-testid={`edit-${item.id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-1" />Edit
                      </Button>
                      <Button size="sm" variant="outline"
                        className={`border-[#262626] h-8 ${item.is_featured ? 'text-[#FF3B30]' : 'text-gray-300'}`}
                        onClick={() => toggleFeatured(item)} data-testid={`feature-${item.id}`}>
                        {item.is_featured ? <><StarOff className="w-3.5 h-3.5 mr-1" />Unfeature</> : <><Star className="w-3.5 h-3.5 mr-1" />Feature</>}
                      </Button>
                      <Button size="sm" variant="outline" className="border-[#262626] text-white h-8" onClick={() => togglePublished(item)}>
                        {item.is_published ? <><EyeOff className="w-3.5 h-3.5 mr-1" />Unpublish</> : <><Eye className="w-3.5 h-3.5 mr-1" />Publish</>}
                      </Button>
                      <Link to={`/interviews/${item.slug || item.id}`} target="_blank">
                        <Button size="sm" variant="ghost" className="text-gray-300 h-8"><ExternalLink className="w-3.5 h-3.5" /></Button>
                      </Link>
                      <Button size="sm" variant="ghost" className="text-red-400 h-8" onClick={() => handleDelete(item)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit / Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editing ? 'Edit Interview' : 'New Interview'}
            </DialogTitle>
          </DialogHeader>
          {error && <div className="bg-red-500/10 border border-red-500/40 text-red-400 px-3 py-2 rounded text-sm">{error}</div>}

          <form onSubmit={handleSave} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Driver Name *</Label>
                <Input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} required
                  className="bg-[#1F1F1F] border-[#262626] text-white" data-testid="interview-name-input" />
              </div>
              <div>
                <Label>Team Name</Label>
                <Input value={form.team_name} onChange={(e) => setForm({ ...form, team_name: e.target.value })}
                  placeholder="The Blue Crew" className="bg-[#1F1F1F] border-[#262626] text-white" />
              </div>
              <div className="md:col-span-2">
                <Label>Subtitle / Hook *</Label>
                <Textarea value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value.slice(0, 240) })}
                  placeholder="From Buick Regals to a 20-car fleet…" rows={2}
                  className="bg-[#1F1F1F] border-[#262626] text-white" required />
                <p className="text-gray-500 text-xs text-right mt-1">{form.subtitle.length}/240</p>
              </div>
              <div>
                <Label>Location</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Oklahoma" className="bg-[#1F1F1F] border-[#262626] text-white" />
              </div>
              <div>
                <Label>Sponsor Name</Label>
                <Input value={form.sponsor_name} onChange={(e) => setForm({ ...form, sponsor_name: e.target.value })}
                  placeholder="CG Law" className="bg-[#1F1F1F] border-[#262626] text-white" />
              </div>
              <div className="md:col-span-2">
                <Label>Sponsor URL</Label>
                <Input value={form.sponsor_url} onChange={(e) => setForm({ ...form, sponsor_url: e.target.value })}
                  placeholder="https://cglaw.org" className="bg-[#1F1F1F] border-[#262626] text-white" />
              </div>
              <div className="md:col-span-2">
                <Label>Sponsor Tagline</Label>
                <Input value={form.sponsor_tagline} onChange={(e) => setForm({ ...form, sponsor_tagline: e.target.value.slice(0, 240) })}
                  placeholder="Backing The Blue Crew's 2026 season — parts, tires, and more."
                  className="bg-[#1F1F1F] border-[#262626] text-white" data-testid="interview-sponsor-tagline-input" />
                <p className="text-gray-500 text-xs mt-1">Optional one-line description shown under the sponsor name.</p>
              </div>
              <div className="md:col-span-2">
                <Label>Gallery Section Title</Label>
                <Input value={form.gallery_title} onChange={(e) => setForm({ ...form, gallery_title: e.target.value.slice(0, 120) })}
                  placeholder="The Blue Crew in Action"
                  className="bg-[#1F1F1F] border-[#262626] text-white" data-testid="interview-gallery-title-input" />
                <p className="text-gray-500 text-xs mt-1">Heading shown above the photo gallery. Defaults to "{form.team_name || form.driver_name || 'Driver'} in Action".</p>
              </div>
            </div>

            {/* Hero image */}
            <div>
              <Label>Hero Image</Label>
              <div className="flex items-start gap-3 mt-2">
                <div className="w-32 h-32 rounded bg-[#1F1F1F] border border-[#262626] overflow-hidden flex items-center justify-center">
                  {form.hero_image ? (
                    <img src={resolveImageUrl(form.hero_image)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <ImagePlus className="w-8 h-8 text-gray-700" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input ref={heroInputRef} type="file" accept="image/*" onChange={handleHeroFile} className="hidden" />
                  <Button type="button" onClick={() => heroInputRef.current?.click()} variant="outline" className="border-[#262626] text-white">
                    <Upload className="w-4 h-4 mr-2" />{form.hero_image ? 'Replace' : 'Upload'} Hero
                  </Button>
                  <p className="text-gray-500 text-xs">Or paste URL below:</p>
                  <Input value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })}
                    placeholder="https://…" className="bg-[#1F1F1F] border-[#262626] text-white text-sm" />
                </div>
              </div>
            </div>

            {/* Gallery */}
            <div>
              <Label>Gallery Photos ({(form.gallery_images || []).length})</Label>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2 mt-2">
                {(form.gallery_images || []).map((url, idx) => (
                  <div key={idx} className="relative group aspect-square rounded bg-[#1F1F1F] border border-[#262626] overflow-hidden">
                    <img src={resolveImageUrl(url)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeGallery(idx)}
                      className="absolute top-1 right-1 w-6 h-6 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="w-3.5 h-3.5 text-white" />
                    </button>
                  </div>
                ))}
                <label className="aspect-square border-2 border-dashed border-[#262626] hover:border-[#FF3B30] rounded flex flex-col items-center justify-center text-gray-500 hover:text-[#FF3B30] cursor-pointer">
                  <ImagePlus className="w-5 h-5 mb-1" />
                  <span className="text-[10px] uppercase tracking-wider">Add</span>
                  <input ref={galleryInputRef} type="file" multiple accept="image/*" onChange={handleGalleryFiles} className="hidden" />
                </label>
              </div>
            </div>

            {/* Q&A */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label>Questions &amp; Answers ({form.qa.length})</Label>
                <Button type="button" size="sm" variant="outline" className="border-[#262626] text-white" onClick={addQa}>
                  <Plus className="w-3.5 h-3.5 mr-1" />Add Q&amp;A
                </Button>
              </div>
              <div className="space-y-4">
                {form.qa.map((item, i) => (
                  <div key={i} className="bg-[#1F1F1F] border border-[#262626] rounded p-3">
                    <div className="flex items-start gap-2 mb-2">
                      <span className="text-[#FF3B30] font-bold text-sm mt-2 w-8">Q{i + 1}.</span>
                      <Input value={item.q} onChange={(e) => updateQa(i, 'q', e.target.value)}
                        placeholder="Question…" className="bg-[#141414] border-[#262626] text-white flex-1" />
                      <Button type="button" size="sm" variant="ghost" className="text-red-400 h-9" onClick={() => removeQa(i)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <Textarea value={item.a} onChange={(e) => updateQa(i, 'a', e.target.value)}
                      placeholder="Answer…" rows={3} className="bg-[#141414] border-[#262626] text-white" />
                  </div>
                ))}
              </div>
            </div>

            {/* Flags */}
            <div className="flex items-center gap-4 flex-wrap bg-[#1F1F1F] border border-[#262626] rounded p-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} />
                <span className="text-white text-sm">Published</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} />
                <span className="text-white text-sm">Feature on homepage (replaces any other featured)</span>
              </label>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={saving} className="bg-[#FF3B30] hover:bg-[#E6352B]" data-testid="save-interview-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : (editing ? 'Save Changes' : 'Create Interview')}
              </Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-[#262626] text-white">
                Close
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
